import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import WebFont from 'webfontloader';
import { map, shareReplay } from 'rxjs/operators';
import { FontWeight } from '../../../../elements/resource/types/shape.type';
import { Store } from '@ngrx/store';
import { selectCurrentFontFamily } from '../../../store/selector/editor.selector';
import { TextShape } from '../../../shape/shapes/text/text-shape';

export interface Font {
  name: string;
  category: string;
  styles?: string[];
  weights?: string[];
}

export interface FontDescriptor {
  name: string;
  designer: string;
  license: string;
  category: string;
  date_added: string;

  fonts: [
    {
      name: string;
      style: string;
      weight: FontWeight;
      filename: string;
      post_script_name: string;
      full_name: string;
      copyright: string;
    },
  ];
}

@Injectable({
  providedIn: 'root',
})
export class FontService {
  private fontsUrl = 'assets/google_fonts.json'; // Path to the JSON file
  fonts$: Observable<Font[]>;
  fonts: Record<string, FontDescriptor>;
  currentTextshapes: Record<string, TextShape> = {};

  constructor(
    private http: HttpClient,
    private readonly store: Store,
  ) {
    this.fonts$ = this.http.get<Font[]>(this.fontsUrl).pipe(
      map(fonts => fonts.sort((f1, f2) => (f1.name > f2.name ? 1 : -1))),
      shareReplay(1),
    );

    this.fonts$.subscribe(fonts => {
      // -- // -- // console.log('fonts', fonts); // -- // -- //
      if (fonts) {
        this._loadFont('Goldman');
        // this._loadFont('Jakarta Sans');
      }
    });

    this.store.select(selectCurrentFontFamily).subscribe(async fontFamily => {
      // -- // -- // -- // -- //
      await this._loadFont(fontFamily);

      Object.values(this.currentTextshapes).map(shape => {
        shape.fontFamily = fontFamily;
        shape.refreshElement();
        shape.reAlignInCell();
      });
    });
  }

  registerTextShape(shape: TextShape) {
    this.currentTextshapes[shape.IRI] = shape;
  }

  deregisterTextShape(shape: TextShape) {
    delete this.currentTextshapes[shape.IRI];
  }
  descriptors: Record<string, FontDescriptor> = {};

  loadFontDescriptorPromises: Record<string, Promise<FontDescriptor>> = {};

  async loadFontDescriptor(family: string) {
    if (this.loadFontDescriptorPromises[family]) {
      return this.loadFontDescriptorPromises[family];
    }

    try {
      console.log('load-font-family', family);
      const promise = this.http
        .get<FontDescriptor>(
          `assets/fonts/${family
            .split(' ')
            .map(word => word.toLowerCase())
            .join('_')}.json`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .toPromise();

      console.log('setPromise', { family, promise });
      this.loadFontDescriptorPromises[family] = promise;

      return promise;
    } catch (error) {
      console.log('load-error', error.message);
      return Promise.reject();
    }
  }

  async waitForFontLoad(family: string) {
    return this.loadFontPromises[family];
  }

  loadFontPromises: Record<string, Promise<boolean>> = {};

  async loadFontFamily(family: string, fontDescriptor: FontDescriptor) {
    // console.log('loadFontFamily', family);
    if (this.loadFontPromises[family]) {
      return this.loadFontPromises[family];
    }

    this.fontsToLoad[family] = true;

    const _weights = fontDescriptor.fonts
      ?.map(({ weight, style }) =>
        style == 'italic' ? `${weight}italic` : weight,
      )
      .join(',');

    const weights = ['400', '700'].join(',');

    const _family =
      fontDescriptor.fonts?.length > 1 ? `${family}:${weights}` : family;
    console.log('loadFontFamily', _family);
    WebFont?.load(
      {
        google: {
          families: [_family], // Specify weights 400 (regular) and 700 (bold)
        },
        active() {
          // console.log('active');
        },
        inactive(e) {
          // console.log('inactive', e);
        },
      },
      // () => {
      //   console.log('active');
      // },
      e => {
        console.log('inactive', e);
      },
    );

    let cnt = 0;
    const MAX_RETRIES = 30;
    const CHECK_INTERVAL_MS = 100;
    this.loadFontPromises[family] = new Promise<boolean>(resolve => {
      const interval = setInterval(() => {
        // Dirty hack for the tests
        const fontLoaded =
          !document.fonts || document.fonts.check(`12px '${family}'`);

        // -- //
        if (fontLoaded) {
          console.log('fontLoaded', family);
          delete this.fontsToLoad[family];
          resolve(true);
          clearInterval(interval);
          return;
        }
        if (cnt == 15) {
          WebFont.load({
            google: {
              families: [_family], // Specify weights 400 (regular) and 700 (bold)
            },
          });
        }

        if (cnt == MAX_RETRIES) {
          resolve(true);
          clearInterval(interval);
        }
        cnt++;
      }, CHECK_INTERVAL_MS);
    });

    return this.loadFontPromises[family];
  }

  defaultFonts = [
    'Arial',
    'Verdana',
    'Tahoma',
    'Times New Roman',
    'Georgia',
    'Courier New',
    'Trebuchet MS',
    'Comic Sans MS',
    'sans-serif',
    'serif',
    'monospace',
  ];

  fontsToLoad: Record<string, boolean> = {};
  async _loadFont(family: string) {
    if (!family) {
      return Promise.resolve();
    }
    if (this.defaultFonts.includes(family)) {
      return;
    }

    // if (this.fontsToLoad[family]) {
    //   return Promise.resolve();
    // }

    const fontDescriptor = await this.loadFontDescriptor(family);
    // console.log(family, 'fontDescriptor', fontDescriptor);
    this.descriptors[family] = fontDescriptor;
    await this.loadFontFamily(family, fontDescriptor);
  }

  loadFont(font: Font, cb: () => void): void {
    if (this.defaultFonts.includes(font.name)) {
      return;
    }
    if (!font || !font.name) return;

    const weights = font.weights?.length ? font.weights.join(',') : '400';
    WebFont.load({
      google: {
        families: [`${font.name}:${weights}`], // Load specific weights
      },
      active: () => {
        console.log(`Font ${font.name} loaded successfully!`);
        cb?.();
      },
    });
  }
}
